import React, { useState, useEffect } from 'react';
import { generateWorkoutSummary } from '../../services/workoutSummaryService';
import BodyDiagram from './BodyDiagram';
import PRBadges from './PRBadges';
import { colors as theme } from '../../theme';

const colors = {
  bg: theme.bg,
  surface1: theme.bg,
  surface2: theme.surface1,
  surface3: theme.surface3,
  border: 'rgba(255, 255, 255, 0.08)',
  text: theme.text,
  textMuted: theme.textMuted,
  accent: theme.gold,
  success: theme.success,
  error: theme.danger,
};

const typography = {
  h2: { fontSize: '20px', fontWeight: '700', lineHeight: '1.3' },
  h3: { fontSize: '18px', fontWeight: '600', lineHeight: '1.4' },
  body: { fontSize: '15px', fontWeight: '400', lineHeight: '1.6' },
  small: { fontSize: '13px', fontWeight: '500', lineHeight: '1.5' },
  tiny: { fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px' },
};

const WorkoutSummaryModal = ({ sessionId, userId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId || !userId) return;

    let cancelled = false;

    const loadSummary = async () => {
      try {
        setLoading(true);
        const data = await generateWorkoutSummary(sessionId, userId);
        if (!cancelled) {
          setSummaryData(data);
          setError(null);
        }
      } catch (err) {
        console.error('Error loading workout summary:', err);
        if (!cancelled) setError('Failed to load workout summary');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadSummary();

    // Auto-dismiss after 15s if still loading (hung network request)
    const timeout = setTimeout(() => {
      cancelled = true;
      onClose();
    }, 15000);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [sessionId, userId, onClose]);

  return (
    <div style={styles.overlay}>
      <style>{cssAnimations}</style>
      <div style={styles.modal}>
        {/* Always-visible close button so users are never trapped */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            color: colors.textMuted,
            fontSize: '20px',
            cursor: 'pointer',
            lineHeight: 1,
            padding: '4px 8px',
          }}
          aria-label="Close"
        >
          ×
        </button>

        {loading && (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner} />
            <p style={{ ...typography.body, color: colors.textMuted, marginTop: '16px' }}>
              Preparing your workout summary...
            </p>
          </div>
        )}

        {error && (
          <div style={styles.errorContainer}>
            <p style={{ ...typography.body, color: colors.error }}>{error}</p>
            <button onClick={onClose} style={styles.closeButton}>
              Close
            </button>
          </div>
        )}

        {!loading && !error && summaryData && (
          <div>
            {/* Header */}
            <div style={styles.header}>
              <div>
                <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.08em', color: colors.accent, marginBottom: '6px' }}>
                  SESSION COMPLETE
                </div>
                <h2 style={{ ...typography.h2, color: colors.text, margin: 0 }}>
                  {summaryData.workoutTitle}
                </h2>
                <p style={{ ...typography.small, color: colors.textMuted, marginTop: '4px' }}>
                  {new Date(summaryData.session.completed_at).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </p>
              </div>
              {summaryData.streak > 0 && (
                <div style={styles.streakBadge}>
                  🔥 {summaryData.streak} week streak
                </div>
              )}
            </div>

            {/* New personal bests — the reward moment (item 12). Only shown
                when there's something real to celebrate, not every session. */}
            {summaryData.prs && summaryData.prs.length > 0 && (
              <PRBadges prs={summaryData.prs} />
            )}

            {/* Total Volume Metric */}
            <div style={styles.volumeCard}>
              <div style={styles.volumeMain}>
                <span style={{ ...typography.h2, fontSize: '32px', color: colors.accent, fontWeight: '700' }}>
                  {summaryData.metrics.totalVolume.toLocaleString()}
                </span>
                <span style={{ ...typography.tiny, color: colors.textMuted, marginLeft: '8px', fontSize: '14px' }}>
                  KG
                </span>
              </div>
              <span style={{ fontSize: '12px', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px' }}>
                TOTAL VOLUME
              </span>
              {summaryData.comparison?.likeForLike && (() => {
                // Last time against this time, on the exercises done both times. Up is green; down is not red,
                // since a lighter session is often the plan (owner, 20 October 2026)
                const { percent, sharedExercises, exercisesThisTime, previousDate } = summaryData.comparison.likeForLike;
                const same = Math.abs(percent) < 2;
                const when = previousDate
                  ? new Date(previousDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                  : null;
                return (
                  <div style={{ marginTop: '12px', textAlign: 'center' }}>
                    <span style={{
                      ...typography.small,
                      color: same ? colors.textMuted : (percent > 0 ? colors.success : colors.text),
                      fontWeight: '600',
                      fontSize: '15px',
                    }}>
                      {same ? 'About the same' : `${percent > 0 ? '↑' : '↓'} ${Math.abs(percent)}%`}
                    </span>
                    <span style={{ ...typography.small, color: colors.textMuted, fontSize: '13px', marginLeft: '6px' }}>
                      {`vs. last time${when ? ` (${when})` : ''}`}
                    </span>
                    {sharedExercises < exercisesThisTime && (
                      <div style={{ ...typography.small, color: colors.textMuted, fontSize: '12px', marginTop: '4px' }}>
                        {`On the ${sharedExercises} exercises you did both times`}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Duration + Working Sets — already computed by the summary
                service but never rendered until now */}
            <div style={styles.metricsGrid}>
              <div style={styles.metricCard}>
                <span style={{ fontSize: '24px', fontWeight: '700', color: colors.text, fontVariantNumeric: 'tabular-nums' }}>
                  {summaryData.metrics.duration === 'N/A' ? '—' : summaryData.metrics.duration}
                </span>
                <span style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px' }}>
                  Duration
                </span>
              </div>
              <div style={styles.metricCard}>
                <span style={{ fontSize: '24px', fontWeight: '700', color: colors.text, fontVariantNumeric: 'tabular-nums' }}>
                  {summaryData.metrics.totalSets}
                </span>
                <span style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px' }}>
                  Working Sets
                </span>
              </div>
            </div>

            {/* Calories Card */}
            {summaryData.metrics.estimatedCalories > 0 && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                backgroundColor: colors.surface2,
                borderRadius: '20px',
                border: `1px solid ${colors.border}`,
                marginBottom: '24px',
                textAlign: 'center',
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '32px', fontWeight: '700', color: '#FF9F0A' }}>
                    ~{summaryData.metrics.estimatedCalories.toLocaleString()}
                  </span>
                  <span style={{ fontSize: '14px', color: colors.textMuted }}>KCAL</span>
                </div>
                <span style={{ fontSize: '12px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px' }}>
                  ESTIMATED CALORIES
                </span>
                <span style={{ fontSize: '11px', color: colors.textMuted, marginTop: '6px', fontStyle: 'italic' }}>
                  {summaryData.metrics.calorieHasCardio && !summaryData.metrics.calorieHasResistance
                    ? 'Based on MET formula · machine display may vary'
                    : summaryData.metrics.calorieHasResistance && !summaryData.metrics.calorieHasCardio
                    ? 'Based on total volume lifted'
                    : 'Combined cardio + resistance estimate'}
                </span>
              </div>
            )}

            {/* Body Diagram */}
            <BodyDiagram muscleGroups={summaryData.muscleGroups} />

            {/* Muscle Groups Preview */}
            <div style={styles.muscleSection}>
              <h3 style={{ ...typography.h3, color: colors.text, margin: '0 0 12px 0' }}>
                Muscles Worked
              </h3>
              <div style={styles.muscleTagsContainer}>
                {summaryData.muscleGroups.slice(0, 6).map((muscle, index) => (
                  <span key={index} style={styles.muscleTag}>
                    {muscle}
                  </span>
                ))}
                {summaryData.muscleGroups.length > 6 && (
                  <span style={styles.muscleTag}>
                    +{summaryData.muscleGroups.length - 6} more
                  </span>
                )}
              </div>
            </div>

            <p style={{ fontSize: '14px', color: colors.textMuted, textAlign: 'center', margin: '0 0 20px', lineHeight: 1.5 }}>
              Strong work. Recover and come back better.
            </p>

            {/* Close Button */}
            <button onClick={onClose} style={styles.closeButton}>
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const cssAnimations = `
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @keyframes celebrate-pop {
    0% { transform: scale(0.8); opacity: 0; }
    60% { transform: scale(1.05); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  }
`;

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    position: 'relative',
    backgroundColor: colors.surface1,
    borderRadius: '20px',
    maxWidth: '500px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    padding: '32px',
    border: `1px solid ${colors.border}`,
    animation: 'celebrate-pop 0.4s cubic-bezier(0.34,1.56,0.64,1)',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
  },
  spinner: {
    width: '44px',
    height: '44px',
    border: `3px solid ${colors.surface2}`,
    borderTop: `3px solid ${colors.accent}`,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '40px 20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    gap: '16px',
  },
  streakBadge: {
    padding: '8px 16px',
    backgroundColor: colors.accent,
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    color: colors.bg,
    whiteSpace: 'nowrap',
  },
  volumeCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '28px 20px',
    backgroundColor: colors.surface2,
    borderRadius: '20px',
    border: `1px solid ${colors.border}`,
    marginBottom: '24px',
    textAlign: 'center',
  },
  volumeMain: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  metricCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backgroundColor: colors.surface2,
    borderRadius: '14px',
    border: `1px solid ${colors.border}`,
    textAlign: 'center',
  },
  muscleSection: {
    padding: '20px',
    backgroundColor: colors.surface2,
    borderRadius: '14px',
    border: `1px solid ${colors.border}`,
    marginBottom: '24px',
  },
  muscleTagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  muscleTag: {
    padding: '6px 12px',
    backgroundColor: colors.surface3,
    borderRadius: '8px',
    fontSize: '12px',
    color: colors.text,
    fontWeight: '500',
  },
  closeButton: {
    width: '100%',
    padding: '14px',
    backgroundColor: colors.accent,
    color: colors.bg,
    border: 'none',
    borderRadius: '14px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
};

export default WorkoutSummaryModal;
